// packages/decision-ui/src/components/DiscoveryHub/TopologyMapper.tsx

import React from 'react';

export interface TopologyMapperProps {
  radiatorCount: number;
  propertyFootprint: number;
  estimatedHeatLossKw: number;
  recommendedPlan: 'Y-Plan' | 'S-Plan' | 'W-Plan';
  planLabel: string;
  planRationale: string;
  onChange: (radiatorCount: number, propertyFootprint: number) => void;
}

const TopologyMapper: React.FC<TopologyMapperProps> = ({
  radiatorCount,
  propertyFootprint,
  estimatedHeatLossKw,
  recommendedPlan,
  planLabel,
  planRationale,
  onChange,
}) => {
  const handleRadiatorCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    onChange(value, propertyFootprint);
  };

  const handlePropertyFootprintChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    onChange(radiatorCount, value);
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'Y-Plan':
        return '#17a2b8'; // info blue
      case 'S-Plan':
        return '#ffc107'; // warning yellow
      case 'W-Plan':
        return '#dc3545'; // danger red
      default:
        return '#6c757d'; // secondary grey
    }
  };

  const getPlanIcon = (plan: string) => {
    switch (plan) {
      case 'Y-Plan':
        return '⚡'; // Simple/streamlined
      case 'S-Plan':
        return '🎯'; // Advanced/targeted
      case 'W-Plan':
        return '👑'; // Premium
      default:
        return '📍';
    }
  };

  return (
    <div style={{
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      padding: '1.5rem',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    }}>
      <h3 style={{ 
        fontSize: '1.25rem', 
        fontWeight: 600, 
        marginTop: 0,
        marginBottom: '0.5rem',
      }}>
        🗺️ Topology Mapper
      </h3>
      <p style={{ 
        fontSize: '0.875rem', 
        color: '#6c757d', 
        marginBottom: '1.5rem',
      }}>
        System volume determines the optimal control plan (S-Plan vs Y-Plan)
      </p>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '1.5rem',
        marginBottom: '1.5rem',
      }}>
        {/* Radiator Count Input */}
        <div>
          <label style={{ 
            display: 'block', 
            fontWeight: 600, 
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
          }}>
            Total Radiator Count
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="range"
              min="1"
              max="30"
              step="1"
              value={radiatorCount}
              onChange={handleRadiatorCountChange}
              style={{
                width: '100%',
                height: '8px',
                borderRadius: '4px',
                background: 'linear-gradient(to right, #007bff 0%, #007bff 100%)',
                outline: 'none',
                WebkitAppearance: 'none',
              }}
            />
            <div style={{ 
              textAlign: 'center', 
              fontSize: '1.5rem', 
              fontWeight: 700,
              marginTop: '0.5rem',
              color: '#007bff',
            }}>
              {radiatorCount} radiators
            </div>
          </div>
        </div>

        {/* Property Footprint Input */}
        <div>
          <label style={{ 
            display: 'block', 
            fontWeight: 600, 
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
          }}>
            Property Footprint (m²)
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="range"
              min="50"
              max="400"
              step="10"
              value={propertyFootprint}
              onChange={handlePropertyFootprintChange}
              style={{
                width: '100%',
                height: '8px',
                borderRadius: '4px',
                background: 'linear-gradient(to right, #007bff 0%, #007bff 100%)',
                outline: 'none',
                WebkitAppearance: 'none',
              }}
            />
            <div style={{ 
              textAlign: 'center', 
              fontSize: '1.5rem', 
              fontWeight: 700,
              marginTop: '0.5rem',
              color: '#007bff',
            }}>
              {propertyFootprint} m²
            </div>
          </div>
        </div>
      </div>

      {/* Estimated Heat Loss */}
      <div style={{
        padding: '1rem',
        backgroundColor: '#e7f3ff',
        borderRadius: '6px',
        marginBottom: '1.5rem',
        border: '1px solid #b3d9ff',
      }}>
        <div style={{ 
          fontSize: '0.875rem', 
          fontWeight: 600,
          marginBottom: '0.25rem',
          color: '#495057',
        }}>
          Estimated Heat Loss
        </div>
        <div style={{ 
          fontSize: '1.25rem', 
          fontWeight: 700,
          color: '#007bff',
        }}>
          {estimatedHeatLossKw} kW
        </div>
      </div>

      {/* Recommended Plan Card */}
      <div style={{
        borderTop: '1px solid #e9ecef',
        paddingTop: '1.5rem',
      }}>
        <h4 style={{ 
          fontSize: '0.875rem', 
          fontWeight: 600, 
          marginBottom: '1rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: '#495057',
        }}>
          Recommended Control Plan
        </h4>
        
        <div style={{
          padding: '1.5rem',
          borderRadius: '8px',
          backgroundColor: '#fff',
          border: `3px solid ${getPlanColor(recommendedPlan)}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1rem',
            marginBottom: '1rem',
          }}>
            <div style={{ 
              fontSize: '2.5rem',
            }}>
              {getPlanIcon(recommendedPlan)}
            </div>
            <div>
              <div style={{ 
                fontSize: '1.5rem', 
                fontWeight: 700,
                color: getPlanColor(recommendedPlan),
                marginBottom: '0.25rem',
              }}>
                {recommendedPlan}
              </div>
              <div style={{ 
                fontSize: '1rem', 
                fontWeight: 600,
                color: '#495057',
              }}>
                {planLabel}
              </div>
            </div>
          </div>
          
          <div style={{ 
            fontSize: '0.875rem', 
            color: '#6c757d',
            lineHeight: '1.5',
          }}>
            {planRationale}
          </div>
        </div>

        {/* Plan Comparison Info */}
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          fontSize: '0.75rem',
          color: '#6c757d',
        }}>
          <div style={{ marginBottom: '0.5rem' }}>
            <strong>Y-Plan:</strong> Best for {'<'}19kW systems. Single motorized valve controls both heating and hot water.
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <strong>S-Plan:</strong> Best for 19-35kW systems. Independent zone control with separate valves.
          </div>
          <div>
            <strong>W-Plan:</strong> Best for {'>'}35kW systems. Multiple heating zones plus hot water priority.
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopologyMapper;
